<template>
  <div class="health-report-page w-100">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">Báo cáo Phân tích sức khỏe toàn diện</h4>
      <b-button class="btn btn-light" :disabled="loading" @click="loadList">
        Làm mới
      </b-button>
    </div>

    <!-- b-overlay là pattern loading chung của hệ thống (xem ActivityDisplayDayList.vue và các
         trang danh sách khác) — spinner hiện giữa vùng bảng thay vì chỉ ở icon nhỏ trên nút
         "Làm mới" (user note: loading nằm trên góc user không để ý, phải nằm ngay chỗ bảng). -->
    <b-overlay :show="loading">
      <b-table
        :key="tableKey"
        :items="reports"
        :fields="fields"
        hover
        responsive
        show-empty
        empty-text="Chưa có báo cáo nào"
      >
        <template #cell(patientName)="{ item }">
          <span class="font-weight-bold">{{ item.patientName || '—' }}</span>
          <div class="text-muted small">{{ item.companyName }}</div>
        </template>
        <template #cell(examDate)="{ item }">
          {{ item.examDate ? convertTimestampToDateNoTime(item.examDate) : '—' }}
        </template>
        <template #cell(state)="{ item }">
          <b-badge :variant="stateOf(item).variant">{{ stateOf(item).label }}</b-badge>
        </template>
        <template #cell(actions)="{ item }">
          <b-button size="sm" class="btn btn-light-primary" @click="openDetail(item)">
            Xem &amp; nhận xét
          </b-button>
        </template>
      </b-table>
    </b-overlay>

    <!-- Chi tiết 1 báo cáo — mở dạng modal full màn hình thay vì render dưới bảng, vì bảng có thể
         dài nên phần chi tiết nằm bên dưới bị khuất, user bấm "Xem & nhận xét" không thấy gì xảy ra
         (user report: "nằm bên dưới nên ko để ý ko thấy"). -->
    <b-modal
      v-if="selected"
      visible
      size="xl"
      scrollable
      hide-footer
      body-class="p-4"
      @hidden="closeDetail"
    >
      <template #modal-title>
        {{ selected.patientName }}
        <span class="text-muted small font-weight-normal ml-2">
          {{ selected.companyName }} · Version {{ selected.version }}
        </span>
        <b-badge :variant="stateOf(selected).variant" class="ml-2">{{ stateOf(selected).label }}</b-badge>
        <span v-if="selected.lastPreviewedVersion >= selected.version" class="text-success small ml-2">
          ✓ Đã xem (v{{ selected.lastPreviewedVersion }})
        </span>
      </template>

      <div class="d-flex justify-content-end align-items-center mb-3">
        <!-- "Đánh dấu đã xem" là hành động có tính kết thúc (khác "Lưu ghi chú" — thao tác giữa
             chừng) nên tách xa khỏi vùng chỉnh sửa, và bắt buộc xác nhận để tránh bấm nhầm
             (PO note: 2 nút cạnh nhau, cùng size/màu nhạt, rất dễ nhầm). -->
        <b-button
          size="sm"
          class="btn btn-light-success mr-2"
          :disabled="markingPreviewed || selected.lastPreviewedVersion >= selected.version"
          @click="confirmMarkPreviewed"
        >
          <b-spinner v-if="markingPreviewed" small class="mr-1" />
          Đánh dấu đã xem
        </b-button>
        <!-- GAP2 — nút KÝ/DUYỆT động theo BƯỚC hiện tại của quy trình 3 bước (BS1 -> BS2 -> Duyệt
             cuối). Nhãn + màu đổi theo approvalFlow.actionLabel. Chỉ hiện khi báo cáo chưa ở trạng
             thái kết thúc. Điều kiện bật: đã xem bản hiện tại (lastPreviewedVersion >= version) —
             mỗi bước ký buộc người ký tự xem lại bản mới. BE vẫn guard lại lần nữa.
             GIỚI HẠN MVP: không xác định/ phân biệt role người ký ở FE — bất kỳ ai đang xem đều
             thấy nút của bước hiện tại (BE cũng không ép role/người ở giai đoạn này). -->
        <b-button
          v-if="canApprove"
          size="sm"
          :class="approvalFlow.isFinal ? 'btn btn-success mr-2' : 'btn btn-primary mr-2'"
          :disabled="approving || selected.lastPreviewedVersion < selected.version"
          :title="selected.lastPreviewedVersion < selected.version ? 'Cần bấm \'Đánh dấu đã xem\' bản hiện tại trước khi ký/duyệt' : ''"
          @click="confirmApprove"
        >
          <b-spinner v-if="approving" small class="mr-1" />
          {{ approvalFlow.actionLabel }}
        </b-button>
      </div>

      <!-- GAP2 — tiến trình chốt báo cáo 3 BƯỚC: BS1 ký -> BS2 ký (kiểm tra chéo) -> Duyệt cuối.
           Chỉ sau BƯỚC 3 (APPROVED) gate gửi thông báo KH mới mở. Mỗi bước tick xanh khi đã hoàn tất. -->
      <div class="health-report-stepper d-flex align-items-center mb-3">
        <div
          v-for="(step, idx) in approvalFlow.steps"
          :key="step.key"
          class="d-flex align-items-center"
        >
          <div
            class="hr-step d-flex align-items-center"
            :class="{ 'hr-step-done': step.done, 'hr-step-current': step.current }"
          >
            <span class="hr-step-dot mr-2">
              <i v-if="step.done" class="fas fa-check"></i>
              <span v-else>{{ idx + 1 }}</span>
            </span>
            <span class="hr-step-label">{{ step.label }}</span>
          </div>
          <span v-if="idx < approvalFlow.steps.length - 1" class="hr-step-sep mx-2">›</span>
        </div>
      </div>

      <div v-if="conflictWarning" class="alert alert-warning d-flex justify-content-between align-items-center">
        <span>
          ⚠ Báo cáo đã được cập nhật bởi người khác kể từ lúc bạn mở — thao tác vừa rồi CHƯA được
          lưu.
        </span>
        <b-button size="sm" class="btn btn-warning ml-2" :disabled="reloadingSelected" @click="reloadSelected">
          <b-spinner v-if="reloadingSelected" small class="mr-1" />
          Tải lại báo cáo này
        </b-button>
      </div>

      <!-- GAP2 — cảnh báo chất lượng dữ liệu, DATA-DRIVEN theo cờ trên bản ghi (không còn cố định
           always-on): mức nặng khi báo cáo dùng template MẪU (isFallbackTemplate), mức nhẹ khi số
           liệu thật nhưng chưa qua validate tự động (isDataValidated=false). PO note: rủi ro lâm
           sàng nếu BS tưởng nhầm số liệu mẫu là kết quả thật, vì tên bệnh nhân hiển thị là tên thật. -->
      <div v-if="selected.isFallbackTemplate" class="alert alert-danger py-2 px-3 mb-3 small">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        <b>⚠️ Dữ liệu chưa đầy đủ — vui lòng kiểm tra lại file khám gốc.</b>
        Báo cáo này KHÔNG dựng được số liệu thật của bệnh nhân nên đang hiển thị
        <b>dữ liệu MẪU của bệnh nhân khác</b>. Chỉ dùng phần <b>"KHUYẾN NGHỊ TỪ DIAB"</b> để nhập
        nhận xét; TUYỆT ĐỐI không dựa vào số liệu hiển thị để ra quyết định lâm sàng. Khi duyệt sẽ
        phải xác nhận đã đối chiếu file gốc.
      </div>
      <div
        v-else-if="!selected.isDataValidated"
        class="alert alert-warning py-2 px-3 mb-3 small"
      >
        <i class="fas fa-exclamation-circle mr-1"></i>
        Một số chỉ số chưa được xác thực tự động (bước validate không hoàn tất) — vui lòng kiểm tra
        kỹ số liệu trước khi duyệt.
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <b-button
          size="sm"
          class="btn btn-light-primary"
          :disabled="savingNotes || !selected.reportHtml"
          @click="saveNotes"
        >
          <b-spinner v-if="savingNotes" small class="mr-1" />
          Lưu ghi chú
        </b-button>
        <b-button size="sm" variant="link" class="p-0" @click="frameExpanded = !frameExpanded">
          {{ frameExpanded ? 'Thu nhỏ' : 'Mở rộng toàn màn hình' }}
        </b-button>
      </div>
      <div class="health-report-frame-wrap mb-3" :class="{ 'health-report-frame-expanded': frameExpanded }">
        <!-- Nút tắt riêng, luôn nổi ở góc overlay — link "Thu nhỏ" phía trên nằm ngoài vùng
             fixed nên khi đã cuộn trang có thể bị che khuất, không phải lúc nào cũng bấm được
             (user note: "thêm nút tắt cho popup preview"). -->
        <b-button
          v-if="frameExpanded"
          variant="light"
          class="health-report-frame-close"
          title="Đóng xem toàn màn hình"
          @click="frameExpanded = false"
        >
          <i class="fas fa-times"></i>
        </b-button>
        <iframe
          v-if="selected.reportHtml"
          ref="reportFrame"
          :srcdoc="selected.reportHtml"
          class="health-report-frame"
          title="Báo cáo"
          sandbox="allow-same-origin"
          @load="bindDiabInputAutoGrow"
        />
        <div v-else class="text-muted p-4 text-center">
          Báo cáo chưa có bản HTML render (ReportHtml rỗng).
        </div>
      </div>

      <h6>Nhận xét đã có</h6>
      <div v-if="!selected.comments || !selected.comments.length" class="text-muted small mb-3">
        Chưa có nhận xét nào.
      </div>
      <div v-for="c in selected.comments" :key="c.id" class="health-comment-item mb-2">
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2">{{ c.authorRole || '—' }}</b-badge>
          <span class="text-muted small">{{ c.pageTitle || c.pageKey }}</span>
          <span class="text-muted small ml-auto">{{ c.createDatetime ? convertTimestampToDateTime(c.createDatetime) : '' }}</span>
        </div>
        <div>{{ c.content }}</div>
      </div>

      <hr />

      <h6>Thêm nhận xét</h6>
      <b-form-group label="Vai trò" label-cols-sm="2">
        <b-form-select
          v-model="commentForm.authorRole"
          :options="['BS1', 'BS2']"
          :disabled="roleLocked"
        />
        <div v-if="roleLocked" class="text-muted small">
          Tự động theo phân công của bạn trên báo cáo này.
        </div>
      </b-form-group>
      <b-form-group label="Trang / mục" label-cols-sm="2">
        <b-form-select v-model="commentForm.pageTitle" :options="REPORT_SECTIONS" />
        <b-form-input
          v-if="commentForm.pageTitle === 'Khác'"
          v-model="commentForm.pageTitleOther"
          class="mt-2"
          placeholder="Nhập tên mục…"
        />
      </b-form-group>
      <b-form-group label="Nội dung" label-cols-sm="2">
        <b-form-textarea v-model="commentForm.content" rows="3" maxlength="2000" placeholder="Nhận xét…" />
        <div class="text-muted small text-right">{{ (commentForm.content || '').length }}/2000 ký tự</div>
      </b-form-group>
      <b-button
        class="btn btn-success"
        :disabled="!commentForm.content || !commentForm.content.trim() || submittingComment"
        @click="submitComment"
      >
        <b-spinner v-if="submittingComment" small class="mr-1" />
        Gửi nhận xét
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import HealthReportService, { HEALTH_REPORT_STATE } from './services/health-report.service';
import { convertTimestampToDateNoTime, convertTimestampToDateTime } from '@/core/utils/dateUtils';

// Danh sách mục/section cố định — tránh free-text tự do làm dữ liệu comment không nhất quán
// (khớp với các nhóm dữ liệu thật của report CarePlus: xem tools/report-template/parse_careplus.py)
const REPORT_SECTIONS = [
  'Tổng quát',
  'Thông số cơ thể',
  'Công thức máu',
  'Mỡ máu',
  'Chức năng gan',
  'Chức năng thận',
  'Đường huyết',
  'Chức năng tuyến giáp',
  'Nước tiểu',
  'Acid uric / Khớp',
  'Tai mũi họng',
  'Mắt',
  'Răng hàm mặt',
  'Siêu âm',
  'X-quang',
  'Điện tâm đồ',
  'Nội soi',
  'Khác',
];

export default {
  name: 'HealthReportReview',
  data() {
    return {
      loading: false,
      reports: [],
      // Ép b-table remount sau mỗi loadList() vì đôi khi diff nội bộ của bootstrap-vue
      // không nhận ra items đã đổi ở lần load đầu, khiến bảng đứng ở empty-state dù data đã có.
      tableKey: 0,
      selected: null,
      conflictWarning: false,
      reloadingSelected: false,
      submittingComment: false,
      markingPreviewed: false,
      approving: false,
      savingNotes: false,
      frameExpanded: false,
      currentAccountId: null,
      roleLocked: false,
      REPORT_SECTIONS,
      commentForm: {
        authorRole: 'BS1',
        pageTitle: '',
        pageTitleOther: '',
        content: '',
      },
      fields: [
        { key: 'patientName', label: 'Bệnh nhân' },
        { key: 'examDate', label: 'Ngày khám' },
        { key: 'state', label: 'Trạng thái' },
        { key: 'actions', label: '', tdClass: 'text-right' },
      ],
    };
  },
  async created() {
    await this.loadList();
    // Deep-link từ trang chi tiết khách hàng BCB Campaign (?reportId=...) — mở thẳng report đó
    // thay vì bắt user tự tìm trong danh sách (PO note: cần link ngược campaign -> report).
    // Cũng chính là cơ chế giữ report đang mở sau khi bấm F5 (xem openDetail() — tự đồng bộ
    // reportId lên URL) — gọi thẳng bằng id, KHÔNG cần report đó có mặt sẵn trong `reports`
    // (report SUPERSEDED/trang sau đổi trang vẫn mở được, không phụ thuộc list hiện tại).
    const reportId = this.$route.query.reportId;
    if (reportId) {
      await this.openDetail({ id: reportId });
    }
    // Lấy accountId user đang login để tự khoá vai trò BS1/BS2 nếu báo cáo đã gán sẵn cho họ —
    // tránh việc bất kỳ ai cũng tự chọn tay vai trò khi gửi nhận xét (PO note: rủi ro data sai vai trò).
    try {
      const user = await this.$mgr.getUser();
      this.currentAccountId = user && user.profile && user.profile.sub;
    } catch (error) {
      console.error(error);
    }
  },
  computed: {
    // GAP1 — chỉ cho duyệt khi báo cáo chưa ở trạng thái kết thúc (đã duyệt/xuất bản/huỷ/bị thay thế).
    canApprove() {
      if (!this.selected) return false;
      const terminal = ['APPROVED', 'PUBLISHED', 'CANCELLED', 'SUPERSEDED'];
      return !terminal.includes(this.selected.state);
    },
    // GAP2 — mô tả tiến trình 3 bước + xác định BƯỚC hiện tại để đổi nhãn nút và nội dung xác nhận.
    // Suy ra từ state (nguồn chân lý) + có tham chiếu bs1/bs2AccountId cho chắc. Phải khớp state
    // machine BE: state trước-BS2 = bước 1 (BS1), PENDING_BS2 = bước 2 (BS2), BS2_DONE = bước 3 (duyệt cuối).
    approvalFlow() {
      const s = (this.selected && this.selected.state) || '';
      const bs1Done = ['PENDING_BS2', 'BS2_DONE', 'APPROVED', 'PUBLISHED'].includes(s)
        || !!(this.selected && this.selected.bs1AccountId);
      const bs2Done = ['BS2_DONE', 'APPROVED', 'PUBLISHED'].includes(s);
      const finalDone = ['APPROVED', 'PUBLISHED'].includes(s);

      // Bước hiện tại (đang chờ hành động): 1 = BS1 ký, 2 = BS2 ký, 3 = duyệt cuối, 0 = đã xong/kết thúc.
      let current = 0;
      if (!finalDone && s !== 'CANCELLED' && s !== 'SUPERSEDED') {
        if (s === 'BS2_DONE') current = 3;
        else if (s === 'PENDING_BS2') current = 2;
        else current = 1;
      }

      const labelByStep = { 1: 'Ký xác nhận (BS1)', 2: 'Ký xác nhận (BS2)', 3: 'Duyệt cuối cùng' };
      return {
        currentStep: current,
        isFinal: current === 3,
        actionLabel: labelByStep[current] || 'Duyệt',
        steps: [
          { key: 'bs1', label: 'BS1 ký', done: bs1Done, current: current === 1 },
          { key: 'bs2', label: 'BS2 ký', done: bs2Done, current: current === 2 },
          { key: 'final', label: 'Duyệt cuối', done: finalDone, current: current === 3 },
        ],
      };
    },
  },
  methods: {
    convertTimestampToDateNoTime,
    convertTimestampToDateTime,
    stateOf(item) {
      return HEALTH_REPORT_STATE[item.state] || { label: item.state, variant: 'secondary' };
    },
    async loadList() {
      this.loading = true;
      try {
        const { data } = await HealthReportService.getPaging({ size: 50 });
        this.reports = data.items || [];
        this.tableKey += 1;
      } catch (error) {
        this.$toastr && this.$toastr.e
          ? this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được danh sách báo cáo' })
          : console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async openDetail(item) {
      this.conflictWarning = false;
      this.frameExpanded = false;
      try {
        const { data } = await HealthReportService.getById(item.id);
        this.selected = data;
        let lockedRole = null;
        if (this.currentAccountId && data.bs1AccountId === this.currentAccountId) lockedRole = 'BS1';
        else if (this.currentAccountId && data.bs2AccountId === this.currentAccountId) lockedRole = 'BS2';
        this.roleLocked = !!lockedRole;
        this.commentForm = {
          authorRole: lockedRole || 'BS1',
          pageTitle: '',
          pageTitleOther: '',
          content: '',
        };
        // Đồng bộ id report đang mở lên URL — trước đây bấm F5 là mất luôn report đang xem, quay
        // về màn danh sách trắng trơn (user note: "bấm F5 để reload lại ko dc"), vì created() chỉ
        // đọc reportId từ URL lúc đầu chứ openDetail() không hề ghi lại. replace (không push) để
        // không làm phình lịch sử back mỗi lần chuyển qua lại giữa các report.
        if (this.$route.query.reportId !== item.id) {
          this.$router.replace({ query: { ...this.$route.query, reportId: item.id } }).catch(() => {});
        }
      } catch (error) {
        console.error(error);
      }
    },
    closeDetail() {
      this.selected = null;
      const query = { ...this.$route.query };
      delete query.reportId;
      this.$router.replace({ query }).catch(() => {});
    },
    // ApiService (core/services/api.service.js) rethrow lỗi HTTP dưới dạng error.data — mất
    // luôn HTTP status gốc (error.status/error.response đều undefined ở đây) — nên KHÔNG thể
    // check error.status === 409 như code cũ (dead code, không bao giờ true). Phải match theo
    // "code" backend trả về (HealthReportController.Update -> Conflict(new { code: "version_conflict" })).
    isVersionConflictError(error) {
      return !!(error && error.code === 'version_conflict');
    },
    // Chỉ tải lại đúng report đang mở (không đụng tới bảng danh sách phía trên) — dùng khi bị
    // conflict 409, thay vì bắt BS bấm "Làm mới" cả danh sách rồi tự tìm lại report (PO note).
    async reloadSelected() {
      if (!this.selected) return;
      this.reloadingSelected = true;
      try {
        const { data } = await HealthReportService.getById(this.selected.id);
        this.selected = data;
        this.conflictWarning = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.reloadingSelected = false;
      }
    },
    // "Đánh dấu đã xem" là hành động có tính kết thúc (khoá luôn nút sau khi bấm) — bắt buộc xác
    // nhận trước để tránh bấm nhầm khi tay đang thao tác gần nút "Lưu ghi chú" (PO note).
    async confirmMarkPreviewed() {
      const confirmed = await this.$swal({
        title: 'Đánh dấu đã xem?',
        text: 'Xác nhận bạn đã xem xong nội dung báo cáo ở phiên bản hiện tại.',
        icon: 'question',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, className: '', closeModal: true },
          confirm: { text: 'Xác nhận', value: true, visible: true, className: 'btn-success', closeModal: true },
        },
      });
      if (confirmed) await this.markPreviewed();
    },
    // Ô "KHUYẾN NGHỊ" đè lên 1 ảnh nền cố định theo toạ độ pixel-perfect nên template đã tắt
    // resize:vertical (kéo tay to hơn ảnh nền rồi lưu là vỡ layout vĩnh viễn — user note: "nội
    // dung comment ... ko đúng nữa"). Để BS vẫn viết dài được, tự động lớn dần theo nội dung —
    // KHÔNG thể nhúng script này vào chính report HTML vì iframe cố tình sandbox="allow-same-
    // origin" (không allow-scripts, lý do bảo mật) nên <script> trong srcdoc bị trình duyệt
    // vô hiệu hoá; phải gắn listener từ phía Vue (parent, cùng-origin nên được phép) sau khi
    // iframe load xong. Vượt quá MAX_H thì cuộn bên trong (CSS overflow-y:auto ở template).
    // TS-16: sau khi report đã qua đủ 3 bước (BS1 ký -> BS2 ký -> Duyệt cuối), các khung
    // "KHUYẾN NGHỊ TỪ DIAB" (.reco-card, data-driven cho MỌI trang hình ảnh — xem
    // intro_pages._reco_card()) mà bác sĩ không điền gì vẫn hiện nguyên placeholder rỗng
    // "Nhập khuyến nghị cho bệnh nhân...", trông như báo cáo còn dang dở dù đã duyệt xong.
    // Ẩn hẳn các khung RỖNG này khi report đã ở trạng thái kết thúc (APPROVED/PUBLISHED) —
    // chỉ ẩn khung trống, KHÔNG đụng khung đã có nội dung bác sĩ đã nhập.
    hideEmptyRecoCardsIfApproved() {
      const frame = this.$refs.reportFrame;
      const doc = frame && frame.contentDocument;
      if (!doc) return;
      const state = this.selected && this.selected.state;
      if (!['APPROVED', 'PUBLISHED'].includes(state)) return;
      doc.querySelectorAll('textarea.diab-input').forEach((ta) => {
        if ((ta.value || ta.textContent || '').trim()) return; // đã có nội dung -> giữ nguyên
        const card = ta.closest('.reco-card');
        if (card) card.style.display = 'none';
      });
    },
    // TS-35 — "Thừa ô khuyến nghị của DiaB": nhiều trang hình ảnh (X-quang, ECG...) có khung
    // "KHUYẾN NGHỊ TỪ DIAB" (.reco-card) mà bác sĩ thấy KHÔNG cần thiết cho bệnh nhân này, muốn
    // BỎ hẳn thay vì để trống hiển thị thừa. hideEmptyRecoCardsIfApproved() chỉ tự ẩn khung RỖNG
    // và chỉ khi đã APPROVED/PUBLISHED — không cho bác sĩ chủ động bỏ trong lúc duyệt. Ở đây gắn
    // nút "×" vào từng .reco-card để bác sĩ tự bỏ ô không cần. Vì iframe sandbox="allow-same-origin"
    // (không allow-scripts) nên phải inject từ Vue (parent) như bindDiabInputAutoGrow. Khi bỏ chỉ
    // set display:none + data-removed trên chính .reco-card -> captureReportHtml() serialize thành
    // style inline nên được LƯU vào reportHtml (giống cơ chế ẩn khung rỗng đã có). Nút "×" và style
    // tiêm vào bị GỠ trước khi serialize (xem captureReportHtml) để không lọt vào HTML lưu/PDF.
    // Không gắn khi report đã PUBLISHED (đã chốt, không cho sửa cấu trúc nữa).
    injectRecoRemoveButtons() {
      const frame = this.$refs.reportFrame;
      const doc = frame && frame.contentDocument;
      if (!doc) return;
      const state = this.selected && this.selected.state;
      if (state === 'PUBLISHED') return;
      const win = doc.defaultView || window;
      if (!doc.getElementById('reco-remove-style')) {
        const st = doc.createElement('style');
        st.id = 'reco-remove-style';
        st.setAttribute('data-reco-injected', '1');
        st.textContent =
          '.reco-remove-btn{position:absolute;top:3pt;right:5pt;width:15pt;height:15pt;'
          + 'line-height:14pt;text-align:center;border:none;border-radius:50%;'
          + 'background:rgba(255,255,255,.9);color:#046459;font-size:12pt;font-weight:700;'
          + 'cursor:pointer;padding:0;z-index:6;box-shadow:0 0 2pt rgba(0,0,0,.35)}'
          + '.reco-remove-btn:hover{background:#D94444;color:#fff}';
        if (doc.head) doc.head.appendChild(st);
      }
      doc.querySelectorAll('.reco-card').forEach((card) => {
        if (card.getAttribute('data-removed') === '1') return;   // đã bỏ -> để nguyên ẩn
        if (card.querySelector(':scope > .reco-remove-btn')) return; // đã gắn nút
        if (win.getComputedStyle(card).position === 'static') card.style.position = 'relative';
        const btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'reco-remove-btn';
        btn.setAttribute('data-reco-injected', '1');
        btn.textContent = '×';
        btn.title = 'Bỏ ô khuyến nghị này khỏi báo cáo';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          card.style.display = 'none';
          card.setAttribute('data-removed', '1');
        });
        card.appendChild(btn);
      });
    },
    bindDiabInputAutoGrow() {
      const frame = this.$refs.reportFrame;
      const doc = frame && frame.contentDocument;
      if (!doc) return;
      this.hideEmptyRecoCardsIfApproved();
      this.injectRecoRemoveButtons();
      const FOOTER_MARGIN = 40; // px — chừa lại đáy trang cho ghi chú/số trang cố định bên dưới card
      // Trần lớn tối đa theo TỪNG trang thay vì 1 số cố định — mỗi trang có lượng khoảng trống
      // dưới card khác nhau (card ECG/siêu âm còn trống nhiều, card khác chật hơn), số cố định
      // 260px luôn dừng sớm dù còn thừa chỗ (user note: "muốn cái box bên dưới phải cao hơn cái
      // text á" — khoảng trắng dưới textarea trong viewport rõ ràng còn nhiều mà không tận dụng).
      const autoGrow = (ta) => {
        const minH = ta.dataset.minH ? parseFloat(ta.dataset.minH) : ta.offsetHeight;
        if (!ta.dataset.minH) ta.dataset.minH = String(minH);
        const page = ta.closest('.page');
        // TS-02/TS-17: ta.offsetTop chỉ đo khoảng cách tới offsetParent GẦN NHẤT (thường là 1 div
        // position:absolute bọc riêng cho từng card, vd '.ipc' wrapper top:150pt), KHÔNG phải tới
        // '.page' — nên maxH tính hụt phần offset đã đi qua ở các ancestor trung gian đó, ra kết
        // quả LỚN HƠN thực tế rất nhiều. Hệ quả: box được phép phình cao vượt xa phần còn trống
        // thật của trang, đè lên footer/số trang cố định (TS-02) và tràn khỏi lề trang không giới
        // hạn (TS-17). Dùng getBoundingClientRect() để lấy khoảng cách THẬT tới '.page', không phụ
        // thuộc offsetParent nào ở giữa.
        let maxH = minH;
        if (page) {
          // Card (.reco-card/.drbox...) có padding/tiêu đề/gạch chia NGOÀI textarea — offsetHeight
          // của riêng textarea không phản ánh phần chrome này, phải trừ thêm vào mức trần cho phép
          // thì viền DƯỚI của cả card mới thật sự dừng trước footer, không riêng gì viền dưới của
          // ô nhập liệu bên trong.
          const card = ta.closest('.reco-card, .drbox') || ta;
          const cardChrome = Math.max(0, card.offsetHeight - ta.offsetHeight);
          const pageRect = page.getBoundingClientRect();
          const cardTop = card.getBoundingClientRect().top;
          // TS-17 (round 2): FOOTER_MARGIN cố định 40px KHÔNG đủ — khối footer thật ('.foot'
          // disclaimer + '.pnum' số trang) cao hơn 40px nên viền dưới card vẫn liếm/đè lên footer khi
          // nội dung dài (QA: "cần sửa lại limit margin để nội dung không bao giờ đè vào footer").
          // Thay vì trừ 1 hằng số từ đáy trang, đo VỊ TRÍ THẬT của footer/số trang nằm DƯỚI card trong
          // cùng '.page' rồi dừng card trước mốc đó 1 khoảng FOOTER_GAP — đảm bảo không đè bất kể
          // footer cao bao nhiêu. Fallback về mốc cũ (đáy trang - FOOTER_MARGIN) khi trang không có
          // phần tử footer nào (hiếm).
          const FOOTER_GAP = 8; // px — khe an toàn giữa viền dưới card và mép trên footer
          let boundaryTop = pageRect.top + page.offsetHeight - FOOTER_MARGIN;
          page.querySelectorAll('.foot, .pnum').forEach((f) => {
            const ft = f.getBoundingClientRect().top;
            if (ft > cardTop && ft < boundaryTop) boundaryTop = ft;
          });
          const availCard = boundaryTop - cardTop - FOOTER_GAP;
          maxH = Math.max(minH, availCard - cardChrome);
        }
        ta.style.height = 'auto';
        ta.style.height = Math.max(minH, Math.min(ta.scrollHeight, maxH)) + 'px';
      };
      doc.querySelectorAll('textarea.diab-input').forEach((ta) => {
        autoGrow(ta);
        ta.addEventListener('input', () => autoGrow(ta));
      });
    },
    // TS-28 — Đồng bộ giá trị BS đang gõ vào text con của <textarea> TRƯỚC khi serialize
    // outerHTML — vì thuộc tính DOM .value KHÔNG tự phản ánh vào HTML khi serialize (textContent
    // giữa <textarea>...</textarea> chỉ là giá trị mặc định ban đầu). Set textContent = value để
    // outerHTML chứa đúng nội dung hiện tại. Chỉ đọc, không phá cấu trúc DOM khác của iframe.
    // Tách riêng khỏi saveNotes() để approve() cũng dùng được (xem note ở approve()).
    captureReportHtml() {
      const frame = this.$refs.reportFrame;
      const doc = frame && frame.contentDocument;
      if (!doc) return null;
      const areas = doc.querySelectorAll('textarea');
      for (let i = 0; i < areas.length; i += 1) {
        areas[i].textContent = areas[i].value;
        // Reset về đúng chiều cao GỐC theo card trước khi lưu — nếu lưu luôn chiều cao đã auto-
        // grow lúc đang gõ thì lần mở sau, bindDiabInputAutoGrow() sẽ đọc offsetHeight (đã to
        // sẵn) làm minH mới, box phình to dần vĩnh viễn qua từng lần lưu (ratchet effect). Chiều
        // cao hiển thị chỉ nên là tiện ích live trong phiên đang xem, không phải state lưu trữ.
        if (areas[i].dataset.minH) areas[i].style.height = `${areas[i].dataset.minH}px`;
      }
      // TS-35 — gỡ mọi phần tử/style do injectRecoRemoveButtons() tiêm vào (nút "×" + <style>)
      // trước khi serialize: chỉ giữ lại KẾT QUẢ của thao tác bỏ ô (display:none + data-removed
      // nằm trên chính .reco-card), không để nút điều khiển lọt vào reportHtml đã lưu / bản in PDF.
      doc.querySelectorAll('[data-reco-injected]').forEach((el) => el.remove());
      const html = doc.documentElement.outerHTML;
      // Gắn lại nút cho phiên đang xem (nếu người dùng chưa lưu/ký, vẫn bỏ tiếp được ô khác).
      this.injectRecoRemoveButtons();
      return html;
    },
    async saveNotes() {
      if (!this.selected) return;
      const reportHtml = this.captureReportHtml();
      if (reportHtml == null) {
        this.$toastr && this.$toastr.e
          ? this.$toastr.e({ title: 'Lỗi', msg: 'Không đọc được nội dung báo cáo' })
          : null;
        return;
      }

      this.savingNotes = true;
      try {
        await HealthReportService.update({
          id: this.selected.id,
          version: this.selected.version,
          reportHtml,
        });
        const { data } = await HealthReportService.getById(this.selected.id);
        this.selected = data;
        this.$toastr && this.$toastr.s
          ? this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu ghi chú' })
          : null;
      } catch (error) {
        if (this.isVersionConflictError(error)) {
          this.conflictWarning = true;
        }
        console.error(error);
      } finally {
        this.savingNotes = false;
      }
    },
    async markPreviewed() {
      if (!this.selected) return;
      this.markingPreviewed = true;
      try {
        await HealthReportService.update({
          id: this.selected.id,
          version: this.selected.version,
          markPreviewed: true,
        });
        const { data } = await HealthReportService.getById(this.selected.id);
        this.selected = data;
        this.$toastr && this.$toastr.s
          ? this.$toastr.s({ title: 'Thành công', msg: 'Đã đánh dấu xem xong' })
          : null;
      } catch (error) {
        if (this.isVersionConflictError(error)) {
          this.conflictWarning = true;
        }
        console.error(error);
      } finally {
        this.markingPreviewed = false;
      }
    },
    // GAP1 — Duyệt báo cáo. Nếu là báo cáo fallback (số liệu mẫu) thì buộc BS xác nhận đã đối
    // chiếu file gốc trước (acknowledgeDataWarning=true) — đúng với guard BE (fallback_not_acknowledged).
    async confirmApprove() {
      if (!this.selected) return;
      const isFallback = !!this.selected.isFallbackTemplate;
      const flow = this.approvalFlow;
      // Nội dung xác nhận theo BƯỚC: bước 1/2 là "ký xác nhận", bước 3 là "duyệt cuối" — chỉ bước 3
      // mới mở gate gửi thông báo KH nên nhấn mạnh hậu quả ở bước này.
      const stepTextByStep = {
        1: 'Bạn xác nhận KÝ (BS1) báo cáo này? Sau khi ký, báo cáo chuyển sang bước chờ BS2 ký kiểm tra chéo.',
        2: 'Bạn xác nhận KÝ (BS2 — kiểm tra chéo) báo cáo này? Sau khi ký, báo cáo chuyển sang bước chờ DUYỆT CUỐI.',
        3: 'Đây là bước DUYỆT CUỐI CÙNG. Sau khi duyệt, báo cáo chuyển sang ĐÃ DUYỆT và MỞ KHOÁ gửi thông báo kết quả cho khách hàng. Xác nhận?',
      };
      const baseText = stepTextByStep[flow.currentStep] || 'Xác nhận?';
      const confirmed = await this.$swal({
        title: `${flow.actionLabel}?`,
        text: isFallback
          ? `Báo cáo này đang dùng DỮ LIỆU MẪU (chưa nối được số liệu thật). Chỉ ký/duyệt sau khi bạn đã tự đối chiếu với file khám gốc.\n\n${baseText}`
          : baseText,
        icon: isFallback ? 'warning' : 'question',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, className: '', closeModal: true },
          confirm: {
            text: isFallback ? `Tôi đã đối chiếu — ${flow.actionLabel}` : flow.actionLabel,
            value: true,
            visible: true,
            className: flow.isFinal ? 'btn-success' : 'btn-primary',
            closeModal: true,
          },
        },
      });
      if (confirmed) await this.approve(isFallback);
    },
    async approve(acknowledgeDataWarning) {
      if (!this.selected) return;
      this.approving = true;
      try {
        // TS-28 — trước đây "Ký" không gửi kèm reportHtml: nếu BS gõ khuyến nghị/nhận xét vào
        // textarea trong khung xem trước rồi bấm thẳng "Ký" (không bấm riêng "Lưu ghi chú"),
        // nội dung vừa gõ chưa từng được lưu xuống DB — sau khi ký, FE getById() lại load về
        // bản HTML CŨ, nhìn như khuyến nghị của BS1 "biến mất" khi report sang trạng thái BS2.
        // Tự động lưu bản hiện tại (nếu đổi khác bản đã lưu) NGAY TRƯỚC khi gọi approve, dùng
        // chung logic serialize với saveNotes() — người ký không cần nhớ bấm 2 nút riêng.
        let version = this.selected.version;
        const reportHtml = this.captureReportHtml();
        if (reportHtml != null && reportHtml !== this.selected.reportHtml) {
          // markPreviewed: true — auto-save bump Version (+1); guard BE (not_previewed) đòi
          // LastPreviewedVersion >= Version, nếu KHÔNG mark previewed ở đây thì approve() ngay
          // sau đó sẽ fail "cần bấm Đánh dấu đã xem" dù người ký chính là người vừa gõ + đang
          // xem nội dung này. Người ký gõ khuyến nghị rồi bấm "Ký" = đã xem bản hiện tại -> set
          // luôn LastPreviewedVersion = Version mới để giữ trải nghiệm 1-click (đúng mục tiêu TS-28).
          const { data: updated } = await HealthReportService.update({
            id: this.selected.id,
            version,
            reportHtml,
            markPreviewed: true,
          });
          if (!updated) {
            this.conflictWarning = true;
            return;
          }
          version = updated.version;
        }
        await HealthReportService.approve(this.selected.id, {
          version,
          acknowledgeDataWarning: !!acknowledgeDataWarning,
        });
        const { data } = await HealthReportService.getById(this.selected.id);
        this.selected = data;
        // Thông báo theo state MỚI sau khi ký/duyệt: PENDING_BS2 = BS1 vừa ký, BS2_DONE = BS2 vừa ký,
        // APPROVED = vừa duyệt cuối (mở gate gửi TB KH).
        const msgByNewState = {
          PENDING_BS2: 'BS1 đã ký. Chờ BS2 ký kiểm tra chéo.',
          BS2_DONE: 'BS2 đã ký. Chờ người duyệt cuối cùng.',
          APPROVED: 'Đã DUYỆT báo cáo — có thể gửi thông báo kết quả cho khách hàng.',
        };
        this.$toastr && this.$toastr.s
          ? this.$toastr.s({ title: 'Thành công', msg: msgByNewState[data.state] || 'Đã cập nhật trạng thái báo cáo' })
          : null;
      } catch (error) {
        if (this.isVersionConflictError(error)) {
          this.conflictWarning = true;
        } else {
          // Map các mã guard BE -> thông báo rõ ràng cho BS (không nuốt lỗi im lặng).
          const msgByCode = {
            not_previewed: 'Bạn cần bấm "Đánh dấu đã xem" ở phiên bản hiện tại trước khi duyệt.',
            fallback_not_acknowledged:
              'Báo cáo dùng dữ liệu mẫu — cần xác nhận đã đối chiếu file gốc trước khi duyệt.',
            invalid_state: 'Báo cáo không ở trạng thái có thể duyệt (có thể đã được duyệt/huỷ).',
            // TS-18 — chiến dịch đã chỉ định trước người phụ trách bước này, không phải tài khoản
            // đang đăng nhập. error.message (BE) đã nói rõ vai trò cần thiết (BS1/BS2/người duyệt).
            not_assigned_reviewer: (error && error.message) || 'Bạn không được chỉ định phụ trách bước này.',
          };
          const msg = (error && msgByCode[error.code]) || (error && error.message) || 'Không duyệt được báo cáo';
          this.$toastr && this.$toastr.e
            ? this.$toastr.e({ title: 'Không duyệt được', msg })
            : null;
        }
        console.error(error);
      } finally {
        this.approving = false;
      }
    },
    async submitComment() {
      if (!this.selected) return;
      this.submittingComment = true;
      try {
        const pageTitle =
          this.commentForm.pageTitle === 'Khác'
            ? this.commentForm.pageTitleOther || 'Khác'
            : this.commentForm.pageTitle || 'Tổng quát';
        await HealthReportService.addComment({
          healthReportId: this.selected.id,
          pageKey: pageTitle.toLowerCase().replace(/\s+/g, '-'),
          pageTitle,
          content: this.commentForm.content,
          authorRole: this.commentForm.authorRole,
        });
        // reload lại đúng report để lấy version + comment mới nhất — tránh optimistic-lock lệch
        const { data } = await HealthReportService.getById(this.selected.id);
        this.selected = data;
        this.commentForm.content = '';
        this.commentForm.pageTitle = '';
        this.commentForm.pageTitleOther = '';
        this.$toastr && this.$toastr.s
          ? this.$toastr.s({ title: 'Thành công', msg: 'Đã gửi nhận xét' })
          : null;
      } catch (error) {
        if (this.isVersionConflictError(error)) {
          this.conflictWarning = true;
        }
        console.error(error);
      } finally {
        this.submittingComment = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.health-report-frame-wrap {
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  overflow: hidden;
  height: 640px;
}
.health-report-frame-expanded {
  position: fixed;
  top: 5vh;
  left: 5vw;
  width: 90vw;
  height: 90vh;
  z-index: 1050;
  background: #fff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.health-report-frame {
  width: 100%;
  height: 100%;
  border: 0;
}
.health-report-frame-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1051;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.health-comment-item {
  padding: 8px 12px;
  background: #f8f9fc;
  border-radius: 8px;
}
.health-report-stepper {
  flex-wrap: wrap;
  gap: 4px 0;
}
.hr-step {
  color: #a1a5b7;
  font-weight: 500;
}
.hr-step-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: #eef0f8;
  color: #a1a5b7;
  border: 1px solid #e4e6ef;
}
.hr-step-done {
  color: #50cd89;
}
.hr-step-done .hr-step-dot {
  background: #e8fff3;
  color: #50cd89;
  border-color: #50cd89;
}
.hr-step-current {
  color: #3699ff;
  font-weight: 700;
}
.hr-step-current .hr-step-dot {
  background: #e1f0ff;
  color: #3699ff;
  border-color: #3699ff;
}
.hr-step-sep {
  color: #d1d3e0;
  font-size: 18px;
}
</style>
